import { useState, useCallback, useRef } from "react";
import { useMutation } from "@apollo/client/react";
import { REQUEST_UPLOAD, CONFIRM_UPLOAD } from "../graphql/aiOperations";

/** Cap on attached files (mirrors backend MAX_EXTRACTION_FILES). Single source of truth —
 *  the composer imports it for its display/disable, rather than re-declaring the number. */
export const maxFiles = 10;

export type UploadFileStatus =
  | "pending"
  | "uploading"
  | "admitted"
  | "rejected"
  | "error";

export interface UploadedFile {
  /** Stable client-side row key — survives concurrent uploads and removals. */
  id: string;
  file: File;
  fileId: string | null;
  status: UploadFileStatus;
  rejectionReason: string | null;
}

interface UseFileUploadResult {
  files: UploadedFile[];
  admittedFileIds: string[];
  addFiles: (incoming: File[]) => Promise<string | null>;
  removeFile: (id: string) => void;
}

const useFileUpload = (): UseFileUploadResult => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  // Synchronous slot reservation. `files.length` lags a pending setFiles, so two
  // rapid addFiles calls would both read the stale count and blow past the cap;
  // this ref is decremented/incremented immediately so the cap holds under races.
  const reservedCountRef = useRef(0);

  const [runRequestUpload] = useMutation(REQUEST_UPLOAD);
  const [runConfirmUpload] = useMutation(CONFIRM_UPLOAD);

  // Update a row by its stable id; a no-op if the row was removed mid-upload.
  const patchRow = useCallback((id: string, patch: Partial<UploadedFile>) => {
    setFiles((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }, []);

  const addFiles = useCallback(
    async (incoming: File[]): Promise<string | null> => {
      const available = maxFiles - reservedCountRef.current;

      if (available <= 0) {
        return `Maximum of ${maxFiles} files allowed.`;
      }

      const toProcess = incoming.slice(0, available);
      reservedCountRef.current += toProcess.length;

      const rows: UploadedFile[] = toProcess.map((file) => ({
        id: crypto.randomUUID(),
        file,
        fileId: null,
        status: "pending",
        rejectionReason: null,
      }));

      setFiles((prev) => [...prev, ...rows]);

      await Promise.all(
        rows.map(async ({ id, file }) => {
          patchRow(id, { status: "uploading" });

          try {
            const { data: requestData } = await runRequestUpload({
              variables: {
                input: {
                  filename: file.name,
                  sizeBytes: file.size,
                  contentType: file.type,
                },
              },
              errorPolicy: "all",
            });

            if (!requestData?.requestUpload) {
              throw new Error("requestUpload returned no data");
            }

            const { fileId, uploadUrl } = requestData.requestUpload;

            const putResponse = await fetch(uploadUrl as string, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type },
            });

            if (!putResponse.ok) {
              throw new Error(
                `S3 PUT failed with status ${putResponse.status}`,
              );
            }

            const { data: confirmData } = await runConfirmUpload({
              variables: { input: { fileId } },
              errorPolicy: "all",
            });

            const confirm = confirmData?.confirmUpload;
            if (!confirm) {
              throw new Error("confirmUpload returned no data");
            }

            if (confirm.status === "ADMITTED") {
              patchRow(id, {
                fileId,
                status: "admitted",
                rejectionReason: null,
              });
            } else {
              patchRow(id, {
                fileId,
                status: "rejected",
                rejectionReason: confirm.rejectionReason ?? null,
              });
            }
          } catch {
            patchRow(id, {
              fileId: null,
              status: "error",
              rejectionReason: null,
            });
          }
        }),
      );

      return null;
    },
    [patchRow, runRequestUpload, runConfirmUpload],
  );

  const removeFile = useCallback(
    (id: string) => {
      // Free the reserved slot in the event handler, NOT inside the setFiles updater:
      // updaters must be pure, and StrictMode double-invokes them in dev, which would
      // double-decrement and let the cap be bypassed. Gate on the current rows so the
      // slot is released only for a row that's actually present.
      if (!files.some((row) => row.id === id)) return;
      reservedCountRef.current -= 1;
      setFiles((prev) => prev.filter((row) => row.id !== id));
    },
    [files],
  );

  const admittedFileIds = files
    .filter((f) => f.status === "admitted" && f.fileId !== null)
    .map((f) => f.fileId as string);

  return { files, admittedFileIds, addFiles, removeFile };
};

export default useFileUpload;
